import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import type { HighlightColor } from '../hooks/useHighlight';

interface Props {
  text: string;
  type: 'sop' | 'bible';
  dayNumber: number;
  questionIndex: number;
  index: number;
  onHighlight: (type: 'sop' | 'bible', dayNumber: number, questionIndex: number, selection: Selection, sopIndex?: number, verseIndex?: number, color?: HighlightColor) => void;
  onRemoveHighlight: (highlightId: string) => void;
  onUpdateHighlightColor: (highlightId: string, color: HighlightColor) => void;
  highlights: Array<{
    id: string;
    startOffset: number;
    endOffset: number;
    color: HighlightColor;
  }>;
  disableHighlighting?: boolean;
}

interface ColorPickerProps {
  onSelect: (action: HighlightColor | 'delete') => void;
  onClose: () => void;
}

const ColorPicker: React.FC<ColorPickerProps> = ({ onSelect, onClose }) => {
  const colors: HighlightColor[] = ['yellow', 'green', 'blue', 'pink', 'purple'];
  
  // Map of colors to their CSS variable names
  const colorMap: Record<HighlightColor, string> = {
    yellow: 'var(--highlight-yellow)',
    green: 'var(--highlight-green)',
    blue: 'var(--highlight-blue)',
    pink: 'var(--highlight-pink)',
    purple: 'var(--highlight-purple)'
  };
  
  const colorPickerContent = (
    <>
      <div 
        className="color-picker-overlay"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'transparent',
          zIndex: 999
        }}
        onClick={onClose}
      />
      <div 
        className="color-picker" 
        style={{ 
          position: 'fixed',
          left: '50%',
          bottom: '20px',
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          padding: '20px',
          background: 'var(--bg-color)',
          borderRadius: '12px',
          border: '1px solid var(--line-break-color)',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          zIndex: 1000
        }}
      >
        {colors.map(color => (
          <button
            key={color}
            onClick={() => {
              onSelect(color);
              onClose();
            }}
            style={{
              width: '40px',
              height: '40px',
              border: '2px solid var(--text-color)',
              borderRadius: '50%',
              background: colorMap[color],
              cursor: 'pointer',
              opacity: 0.8,
              transition: 'transform 0.2s ease, opacity 0.2s ease'
            }}
            onMouseOver={e => {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseOut={e => {
              e.currentTarget.style.opacity = '0.8';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          />
        ))}
        <button
          onClick={() => {
            onSelect('delete');
            onClose();
          }}
          style={{
            width: '40px',
            height: '40px',
            border: '2px solid var(--text-color)',
            borderRadius: '50%',
            background: 'var(--bg-color)',
            cursor: 'pointer',
            opacity: 0.8,
            transition: 'transform 0.2s ease, opacity 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px'
          }}
          onMouseOver={e => {
            e.currentTarget.style.opacity = '1';
            e.currentTarget.style.transform = 'scale(1.1)';
          }}
          onMouseOut={e => {
            e.currentTarget.style.opacity = '0.8';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          🗑️
        </button>
      </div>
    </>
  );

  return ReactDOM.createPortal(
    colorPickerContent,
    document.body
  );
};

// Add CSS variables at the root level
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  :root {
    --highlight-yellow: rgba(255, 255, 0, 0.5);
    --highlight-green: rgba(0, 255, 0, 0.5);
    --highlight-blue: rgba(0, 191, 255, 0.5);
    --highlight-pink: rgba(255, 192, 203, 0.5);
    --highlight-purple: rgba(147, 112, 219, 0.5);
  }

  [data-theme='dark'] {
    --highlight-yellow: rgba(255, 255, 0, 0.35);
    --highlight-green: rgba(0, 255, 0, 0.35);
    --highlight-blue: rgba(0, 191, 255, 0.35);
    --highlight-pink: rgba(255, 192, 203, 0.35);
    --highlight-purple: rgba(147, 112, 219, 0.35);
  }

  .highlighted-yellow { background-color: var(--highlight-yellow) !important; }
  .highlighted-green { background-color: var(--highlight-green) !important; }
  .highlighted-blue { background-color: var(--highlight-blue) !important; }
  .highlighted-pink { background-color: var(--highlight-pink) !important; }
  .highlighted-purple { background-color: var(--highlight-purple) !important; }
`;
document.head.appendChild(styleSheet);

export const HighlightableText: React.FC<Props> = ({
  text,
  type,
  dayNumber,
  questionIndex,
  index,
  onHighlight,
  onRemoveHighlight,
  onUpdateHighlightColor,
  highlights,
  disableHighlighting = false
}) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  // const [colorPickerPosition, setColorPickerPosition] = useState({ x: 0, y: 0 });
  const [activeHighlightId, setActiveHighlightId] = useState<string | null>(null);
  const [showCopyNotification, setShowCopyNotification] = useState(false);
  // const longPressTimeoutRef = useRef<NodeJS.Timeout>();
  // const isLongPressRef = useRef(false);
  const deleteTimeoutRef = useRef<NodeJS.Timeout>();
  const [deletingHighlightId, setDeletingHighlightId] = useState<string | null>(null);
  const LONG_PRESS_DURATION = 500;
  const pressStartTimeRef = useRef<number>(0);

  const handleSelection = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) return;

    if (disableHighlighting) {
      // In copy mode, just copy the selected text
      const selectedText = selection.toString();
      if (selectedText) {
        // Try to copy using the clipboard API first
        const copyText = async () => {
          try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
              await navigator.clipboard.writeText(selectedText);
            } else {
              // Fallback for mobile: Create temporary textarea
              const textArea = document.createElement('textarea');
              textArea.value = selectedText;
              textArea.style.position = 'fixed';
              textArea.style.left = '-9999px';
              document.body.appendChild(textArea);
              textArea.focus();
              textArea.select();
              try {
                document.execCommand('copy');
              } catch (err) {
                console.error('Fallback: Oops, unable to copy', err);
              }
              document.body.removeChild(textArea);
            }
            // Show copy notification
            setShowCopyNotification(true);
            setTimeout(() => {
              setShowCopyNotification(false);
            }, 2000);

            // Highlight the selection temporarily
            const range = selection.getRangeAt(0);
            const span = document.createElement('span');
            span.className = 'copy-feedback';
            range.surroundContents(span);

            // Remove the feedback span after animation
            setTimeout(() => {
              const parent = span.parentNode;
              if (parent) {
                while (span.firstChild) {
                  parent.insertBefore(span.firstChild, span);
                }
                parent.removeChild(span);
              }
              // Clear the selection after the animation
              selection.removeAllRanges();
            }, 500);
          } catch (err) {
            console.error('Failed to copy text:', err);
          }
        };
        copyText();
      }
      return;
    }

    // Only proceed with highlighting if not in copy mode
    const textElement = textRef.current;
    if (!textElement) return;

    // Get the range and validate it's within our text element
    const range = selection.getRangeAt(0);
    
    // Check if selection spans multiple paragraphs - if so, let the global handler deal with it
    const allParagraphs = document.querySelectorAll('.bible-text, .sop-text');
    let affectedParagraphCount = 0;
    allParagraphs.forEach((para) => {
      if (range.intersectsNode(para)) {
        affectedParagraphCount++;
      }
    });
    
    // If selection spans multiple paragraphs, don't handle it here (global handler will)
    if (affectedParagraphCount > 1) {
      return;
    }
    
    // Validate selection is within our text element
    if (!textElement.contains(range.startContainer) || !textElement.contains(range.endContainer)) {
      return;
    }

    // Calculate offsets and create highlight
    onHighlight(
      type,
      dayNumber,
      questionIndex,
      selection,
      type === 'sop' ? index : undefined,
      type === 'bible' ? index : undefined
    );

    // Clear selection only in highlight mode
    selection.removeAllRanges();
  }, [type, dayNumber, questionIndex, index, onHighlight, disableHighlighting]);

  const handleHighlightPress = useCallback((e: MouseEvent | TouchEvent, highlightId: string) => {
    if (disableHighlighting) return; // Ignore all highlight interactions in copy mode
    
    e.preventDefault();
    e.stopPropagation();

    // Clear any existing timeouts
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
    }

    // Record press start time
    pressStartTimeRef.current = Date.now();

    // Set timeout for delete action
    deleteTimeoutRef.current = setTimeout(() => {
      // Only delete if we're still pressing (no mouseup/touchend yet)
      if (pressStartTimeRef.current !== 0) {
        onRemoveHighlight(highlightId);
        setDeletingHighlightId(null);
        pressStartTimeRef.current = 0; // Reset press start time
      }
    }, LONG_PRESS_DURATION);

    // Show visual feedback only for this highlight
    setDeletingHighlightId(highlightId);
  }, [onRemoveHighlight, disableHighlighting]);

  const handleHighlightRelease = useCallback((e: MouseEvent | TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Clear delete timeout and feedback
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
    }

    const pressDuration = pressStartTimeRef.current ? Date.now() - pressStartTimeRef.current : 0;
    const target = e.target as Element;
    const highlightId = target.getAttribute('data-highlight-id');

    // If press was shorter than threshold and we have a highlight ID, show color picker
    if (pressDuration > 0 && pressDuration < LONG_PRESS_DURATION && highlightId) {
      setActiveHighlightId(highlightId);
      setShowColorPicker(true);
    }

    // Reset state
    pressStartTimeRef.current = 0;
    setDeletingHighlightId(null);
  }, []);

  const handleHighlightTouchStart = useCallback((e: TouchEvent, highlightId: string) => {
    handleHighlightPress(e, highlightId);
  }, [handleHighlightPress]);

  const handleHighlightTouchEnd = useCallback((e: TouchEvent) => {
    handleHighlightRelease(e);
  }, [handleHighlightRelease]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const colorPicker = document.querySelector('.color-picker');
      const clickedHighlight = (e.target as Element)?.closest('.highlighted-text');
      
      if (colorPicker && !colorPicker.contains(e.target as Node) && !clickedHighlight) {
        setShowColorPicker(false);
        setActiveHighlightId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleHighlightRelease, handleHighlightTouchEnd, handleHighlightTouchStart]);

  useEffect(() => {
    const textElement = textRef.current;
    if (!textElement) return;

    // Create segments array to handle overlapping highlights
    type Segment = {
      start: number;
      end: number;
      highlight: typeof highlights[0];
    };

    const segments: Segment[] = [];
    const points = new Set<number>();

    // Collect all unique points
    highlights.forEach(h => {
      points.add(h.startOffset);
      points.add(h.endOffset);
    });

    // Convert to sorted array
    const sortedPoints = Array.from(points).sort((a, b) => a - b);

    // Create segments between points
    for (let i = 0; i < sortedPoints.length - 1; i++) {
      const start = sortedPoints[i];
      const end = sortedPoints[i + 1];
      const activeHighlights = highlights.filter(h => 
        h.startOffset <= start && h.endOffset >= end
      );

      if (activeHighlights.length > 0) {
        // Use the highlight with the largest range if there are overlaps
        const primaryHighlight = activeHighlights.reduce((prev, current) => {
          const prevRange = prev.endOffset - prev.startOffset;
          const currentRange = current.endOffset - current.startOffset;
          return currentRange > prevRange ? current : prev;
        });

        segments.push({
          start,
          end,
          highlight: primaryHighlight
        });
      }
    }

    // Create document fragment
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;

    // Add segments and non-highlighted text
    segments.forEach(segment => {
      // Add non-highlighted text before segment
      if (segment.start > lastIndex) {
        fragment.appendChild(
          document.createTextNode(text.slice(lastIndex, segment.start))
        );
      }

      // Add highlighted segment
      const highlightSpan = document.createElement('span');
      highlightSpan.className = `highlighted-text highlighted-${segment.highlight.color}`;
      if (deletingHighlightId === segment.highlight.id) {
        highlightSpan.classList.add('deleting');
      }
      highlightSpan.setAttribute('data-highlight-id', segment.highlight.id);
      highlightSpan.textContent = text.slice(segment.start, segment.end);

      // Only add highlight event listeners if not in copy mode
      if (!disableHighlighting) {
        highlightSpan.addEventListener('mousedown', (e) => handleHighlightPress(e, segment.highlight.id));
        highlightSpan.addEventListener('mouseup', handleHighlightRelease);
        highlightSpan.addEventListener('mouseleave', handleHighlightRelease);
        highlightSpan.addEventListener('touchstart', (e) => handleHighlightTouchStart(e as TouchEvent, segment.highlight.id), { passive: false });
        highlightSpan.addEventListener('touchend', handleHighlightTouchEnd);
        highlightSpan.addEventListener('touchcancel', handleHighlightTouchEnd);
      }

      fragment.appendChild(highlightSpan);
      lastIndex = segment.end;
    });

    // Add remaining text
    if (lastIndex < text.length) {
      fragment.appendChild(
        document.createTextNode(text.slice(lastIndex))
      );
    }

    // Replace content
    textElement.textContent = '';
    textElement.appendChild(fragment);

    return () => {
      textElement.textContent = text;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, highlights, deletingHighlightId, handleHighlightPress, disableHighlighting, handleHighlightRelease, handleHighlightTouchEnd, handleHighlightTouchStart]);


  return (
    <>
      {showCopyNotification && (
        <div className="copy-notification">
          Text copied to clipboard! ✓
        </div>
      )}
      <p 
        ref={textRef} 
        className={`${type === 'sop' ? 'sop-text' : 'bible-text'} no-padding-v ${disableHighlighting ? 'copy-mode' : ''}`}
        onMouseUp={handleSelection}
        onTouchEnd={handleSelection}
      >
        {text}
      </p>
      {!disableHighlighting && showColorPicker && activeHighlightId && (
        <ColorPicker
          onSelect={(color) => {
            if (color === 'delete') {
              onRemoveHighlight(activeHighlightId);
            } else {
              onUpdateHighlightColor(activeHighlightId, color);
            }
            setShowColorPicker(false);
            setActiveHighlightId(null);
          }}
          onClose={() => {
            setShowColorPicker(false);
            setActiveHighlightId(null);
          }}
        />
      )}
    </>
  );
}; 